<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <section :class="containerStyles" ref="timelineRef">
    <div
      class="hidden lg:grid grid-cols-12 lg:col-start-2 lg:col-span-full lg:h-full lg:grid-cols-timeline"
    >
      <div
        class="col-start-1 col-span-full lg:col-start-1 lg:col-span-5 relative lg:mr-10 overflow-hidden flex flex-col justify-center"
      >
        <Transition
          mode="out-in"
          enter-active-class="duration-300 transform ease-out-cubic"
          leave-active-class="duration-100 transform ease-out-cubic"
          :enter-from-class="slideInStyles"
          :leave-to-class="slideOutStyles"
        >
          <div :key="timelineYears[currentIndex].year" class="min-h-[500px]">
            <header4
              :key="timelineYears[currentIndex].year"
              :text="timelineYears[currentIndex].year"
              classes="pb-20"
              :is-umbraco="isUmbraco"
              :theme="themeType"
            />

            <header1
             v-if="timelineYears[currentIndex].title.length > 0"
              :key="timelineYears[currentIndex].title"
              :text="timelineYears[currentIndex].title"
              classes="pb-5"
              :is-umbraco="isUmbraco"
              :theme="themeType"
            />

            <rich-text
              :text="timelineYears[currentIndex].content"
              classes="pb-8"
              :is-umbraco="isUmbraco"
              :theme="themeType"
            />
          </div>
        </Transition>

        <div class="absolute bottom-12">
          <control-button
            @click="prev"
            pos="up"
            :theme-type="'light'"
            classes="mb-3"
            :disabled="currentIndex === 0"
            :is-umbraco="isUmbraco"
          />
          <control-button
            @click="next"
            pos="down"
            :theme-type="'light'"
            :disabled="currentIndex === timelineYears.length - 1"
            :is-umbraco="isUmbraco"
          />
        </div>
      </div>
      <div
        class="col-start-1 col-span-full lg:col-start-6 lg:col-span-6 relative overflow-hidden"
      >
        <div
          v-for="(timeline, index) in timelineYears"
          :key="index"
          :class="[
            'w-full h-full absolute transform duration-500 ease-out-cubic',
            index === currentIndex
              ? ''
              : index < currentIndex
              ? '-translate-y-[20%]'
              : 'translate-y-[100%]',
          ]"
        >
          <image-component
            :img-alt="timeline.title"
            :image="timeline.image"
            :list="false"
            loading="lazy"
            classes="w-full h-full object-cover"
          />
        </div>
      </div>
      <div
        class="lg:col-start-12 lg:col-span-1 bg-ocean lg:flex flex-col justify-center items-center pr-[60px] lg:gap-1 xl:gap-2.5"
      >
        <div
          v-for="(timeline, index) in timelineYears"
          class="relative mb-3"
        >
          <p
            :class="[
              ' w-full h-full text-center text-white transform duration-500 ease-out-cubic font-bold leading-4 text-base-static md:m-static xl:text-l-static',
              index === currentIndex ? 'text-white' : 'text-white-50',
            ]"
          >
            {{ timeline.year }}
          </p>
        </div>
      </div>
    </div>
    <timeline-year
      v-for="(year, index) in timelineYears"
      :key="index"
      :year="year.year"
      :title="year.title"
      :image="year.image"
      :theme-type="themeType"
      :content="year.content"
      :is-umbraco="isUmbraco"
      @handle-click="updateIndex"
    />
  </section>
</template>

<script setup lang="ts">
import { Ref, computed, onMounted, onUnmounted, ref, toRefs, watch } from "vue";
import { onIntersect } from "@/composables/onIntersect";
import { changeTheme, getThemeType } from "@/util/helper";
import {
  ITimelineProps,
  ITimelineYear,
} from "@/types/components/ITimeLineProps";
import ControlButton from "@atoms/ControlButton/ControlButton.vue";
import Header1 from "@components/Typography/Header1.ce.vue";
import Header4 from "@components/Typography/Header4.ce.vue";
import RichText from "@components/Typography/RichText.ce.vue";
import ImageComponent from "@atoms/Image/Img.vue";
import TimelineYear from "@organisms/Timeline/TimelineYear.vue";

const props = withDefaults(defineProps<ITimelineProps>(), {
  isUmbraco: false,
});

const { theme, isUmbraco, years } = toRefs(props);

const observer: Ref<IntersectionObserver | null> = ref(null);
const timelineRef = ref(null);
const currentIndex = ref(0);
const direction = ref<"up" | "down">("up");

const onEnter = () => {
  changeTheme(theme.value);
};
const onExit = () => {};

onMounted(() => {
  observer.value = onIntersect(timelineRef.value, onEnter, onExit, false, {
    threshold: 0.6,
  });
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
});

function next() {
  if (currentIndex.value === timelineYears.value.length - 1) {
    return;
  }
  currentIndex.value++;
}

function prev() {
  if (currentIndex.value === 0) {
    return;
  }
  currentIndex.value--;
}

watch(currentIndex, (newIndex, oldIndex) => {
  if (newIndex > oldIndex) {
    direction.value = "up";
  } else {
    direction.value = "down";
  }
});

const updateIndex = (index: number) => {
  console.log(updateIndex, index);
  currentIndex.value = index;
};

const timelineYears = computed(() => {
  return JSON.parse(years.value) as ITimelineYear[];
});

const themeType = computed(() => {
  return getThemeType(theme.value);
});

const slideInStyles = computed(() => {
  const dir = direction.value === "up" ? "translate-y-6" : "-translate-y-6";
  return `opacity-0 ${dir}`;
});

const slideOutStyles = computed(() => {
  const dir = direction.value === "up" ? "-translate-y-6" : "translate-y-6";
  return `opacity-0 ${dir}`;
});

const containerStyles = computed(() => {
  const bgColor = isUmbraco.value ? `bg-${theme.value}` : "";
  return `${bgColor} sectionContainer lg:content-stretch lg:min-h-screen pt-20 lg:pt-0 pb-20 lg:pb-0`;
});
</script>
